import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { getFacebookImageUrls } from '../../../../modules/actions/mediaFiles'
import {
  facebookImageUrlsWasLoadedSelector,
  facebookRequestedImageHashesSelector
} from '../../../../modules/selectors/mediaFiles'
import { selectedFacebookAdAccountSelector } from '../../../../modules/selectors/adAccounts'

import {
  getMissingImageHashes,
  isAllImageUrlsWasRequested
} from '../../../../forms/Facebook/AdForms/AdFacebookEdit/helpers'

export function useFacebookMediaLoading({ imageHashes }) {
  const dispatch = useDispatch()

  const requestedImageHashes = useSelector(facebookRequestedImageHashesSelector)

  const imageUrlsWasLoaded = useSelector(facebookImageUrlsWasLoadedSelector)
  const { id: selectedAdAccountId } = useSelector(selectedFacebookAdAccountSelector)

  const initialMediaIsLoading = useMemo(
    () => !!imageHashes.length && !imageUrlsWasLoaded,
    [imageHashes, imageUrlsWasLoaded]
  )

  const flatImageHashes = useMemo(() => {
    return imageHashes.reduce((acc, { hashes }) => {
      return acc.concat(hashes)
    }, [])
  }, [imageHashes])

  useEffect(() => {
    if (!imageUrlsWasLoaded && imageHashes.length) {
      dispatch(getFacebookImageUrls(imageHashes))
    }
  }, [dispatch, imageHashes, selectedAdAccountId, imageUrlsWasLoaded])

  // these useEffects were created for ad create/edit form, when new images can be added
  // load more images, if missing hashes appeared - this helps to avoid all images refetching
  useEffect(() => {
    if (
      imageUrlsWasLoaded &&
      !isAllImageUrlsWasRequested({
        hashes: flatImageHashes,
        requestedHashes: requestedImageHashes
      })
    ) {
      const missingHashes = getMissingImageHashes({
        hashes: flatImageHashes,
        requestedHashes: requestedImageHashes
      })

      const formattedMissingHashesWithAccounts = missingHashes.map(hash => {
        const foundImageHashGroup = imageHashes.find(item => item.hashes.includes(hash))

        return {
          hash,
          account: foundImageHashGroup.account
        }
      })

      const groupedMissingHashesByAccount = formattedMissingHashesWithAccounts.reduce((acc, { hash, account }) => {
        if (!acc[account]) {
          acc[account] = []
        }
        acc[account].push(hash)
        return acc
      }, {})

      const missingImageHashes = Object.entries(groupedMissingHashesByAccount).map(([account, hashes]) => ({
        account,
        hashes
      }))

      dispatch(getFacebookImageUrls(missingImageHashes))
    }
  }, [dispatch, imageHashes, flatImageHashes, requestedImageHashes, imageUrlsWasLoaded, selectedAdAccountId])

  return { initialMediaIsLoading }
}
